import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { workouts, auth } from '../services/api';
import { toastErrorMessage } from '../context/ToastContext';
import { audioCaptureSupported, createAudioCapture } from '../services/AssistantAudioCapture';
import { WorkoutVoiceSession, type WorkoutVoiceStatus } from '../utils/workoutVoiceSession';

export type WorkoutPromptVoiceStatus = WorkoutVoiceStatus;
export interface UseWorkoutPromptVoiceOptions {
  userId: number; locale?: string; disabled?: boolean;
  onTranscript: (text: string) => void; onError?: (message: string) => void;
}
export function useWorkoutPromptVoice(options: UseWorkoutPromptVoiceOptions) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<WorkoutVoiceStatus>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const latest = useRef({ ...options, t }); latest.current = { ...options, t };
  const { userId, locale } = options;
  const session = useMemo(() => new WorkoutVoiceSession({
    createCapture: createAudioCapture,
    transcribe: async data => {
      const actorId = userId > 0 ? userId : Number((await auth.me()).data.data.id);
      data.append('user_id', String(actorId)); if (locale) data.append('locale', locale);
      const result = await workouts.transcribePromptAudio(data);
      return String(result.data?.data?.transcript ?? '');
    },
    onStatus: setStatus, onElapsed: setElapsed,
    onTranscript: text => latest.current.onTranscript(text),
    onError: error => {
      const name = error instanceof Error ? error.message : '';
      const permission = error instanceof Error && ['NotAllowedError', 'PermissionDeniedError'].includes(error.name);
      const key = name === 'short' ? 'voiceTooShort' : permission || name === 'permission' ? 'voicePermissionDenied' : 'voiceTranscribeFailed';
      const message = ['short', 'empty', 'recording', 'unsupported', 'permission'].includes(name) || permission
        ? latest.current.t(`workoutBuilder.ai.${key}`) : toastErrorMessage(error, latest.current.t(`workoutBuilder.ai.${key}`));
      setErrorMessage(message); latest.current.onError?.(message);
    },
  }), [userId, locale]);
  useEffect(() => {
    setStatus('idle'); setElapsed(0); setErrorMessage(null);
    return () => { void session.cancel(false); };
  }, [session]);
  const cancelRecording = useCallback(async () => { setErrorMessage(null); await session.cancel(); }, [session]);
  const stopRecording = useCallback(() => session.stop(), [session]);
  const toggleRecording = useCallback(() => {
    if (session.status === 'recording') { void session.stop(); return; }
    if (latest.current.disabled || !audioCaptureSupported()) return;
    setErrorMessage(null); void session.start();
  }, [session]);
  return { status, elapsed, errorMessage, isSupported: audioCaptureSupported(),
    isRecording: status === 'recording', isRequesting: status === 'requesting',
    isTranscribing: status === 'transcribing', cancelRecording, toggleRecording, stopRecording };
}
